import {
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  input,
  output,
  signal,
  untracked,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgTemplateOutlet } from '@angular/common';
import { TrimWhitespaceDirective } from '../../../../core/directives/trim.directive';
import { trimFormGroup } from '../../../../core/utils/form.utils';
import { ApiService } from '../../../../core/services/api.service';
import { ToastService } from '../../../../core/services/toast.service';
import { S3ImageUploadService } from '../../../../core/services/s3-image-upload.service';
import { API_ROUTES } from '../../../../core/constants/api-routes';
import { CreateFamilyGroupRequest, FamilySpaceDetail, LovedOnesFamilySpace, UpdateFamilyGroupRequest } from '../../../../core/models/loved-ones.models';

@Component({
  selector: 'app-create-family',
  templateUrl: './create-family.html',
  styleUrl: './create-family.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule, TrimWhitespaceDirective, NgTemplateOutlet],
})
export class CreateFamily {
  private readonly fb = inject(FormBuilder);
  private readonly api = inject(ApiService);
  private readonly toast = inject(ToastService);
  private readonly s3 = inject(S3ImageUploadService);

  /** Family to edit — null for create mode. */
  readonly editFamily = input<LovedOnesFamilySpace | null>(null);
  /** When true renders as an inline expand panel instead of a modal. */
  readonly isInline = input(false);

  /** Emits when a family group is successfully created/updated. */
  readonly familyCreated = output<void>();
  /** Emits when user cancels / closes. */
  readonly closed = output<void>();

  readonly isLoading = signal(false);
  readonly isLogoUploading = signal(false);
  readonly logoPreview = signal<string | null>(null);
  /** True while fetching the full family space detail for edit mode. */
  readonly isFetchingDetail = signal(false);
  private logoS3Key = '';

  readonly familyForm = this.fb.group({
    familyName:     ['', [Validators.required, Validators.minLength(2), Validators.maxLength(50)]],
    welcomeMessage: ['', [Validators.maxLength(300)]],
  });

  get f() { return this.familyForm.controls; }

  constructor() {
    effect(() => {
      const family = this.editFamily();
      untracked(() => {
        this.familyForm.reset();
        this.logoPreview.set(null);
        this.logoS3Key = '';
        if (family) {
          this.familyForm.patchValue({ familyName: family.familyName ?? '' });
          this.loadFamilySpaceDetail(family.id);
        }
      });
    });
  }

  /** Fetches the full family space detail and pre-populates the form for edit mode. */
  private async loadFamilySpaceDetail(familySpaceId: string): Promise<void> {
    this.isFetchingDetail.set(true);
    try {
      const resp = await this.api.request<FamilySpaceDetail>(
        'GET',
        `${API_ROUTES.GET_FAMILY_SPACE_DETAIL_BY_ID}/${familySpaceId}`,
        undefined,
        { showToaster: false, showLoader: false, useToken: true },
      );
      if (resp.status && resp.data) {
        const d = resp.data;
        this.familyForm.patchValue({
          familyName:     d.familyName     ?? '',
          welcomeMessage: d.welcomeMessage ?? '',
        });
        if (d.displayPictureUrl) {
          this.logoPreview.set(d.displayPictureUrl);
          this.logoS3Key = d.displayPictureName ?? '';
        }
      }
    } catch { /* silent — form retains the minimal data already patched */ }
    finally {
      this.isFetchingDetail.set(false);
    }
  }

  onLogoSelected(event: Event): void {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      this.toast.error('Only image files are allowed.');
      return;
    }

    // Show local preview
    const reader = new FileReader();
    reader.onload = e => this.logoPreview.set(e.target?.result as string);
    reader.readAsDataURL(file);

    // Upload to S3
    this.isLogoUploading.set(true);
    this.s3.upload(file, 'familyLogo').then(result => {
      this.logoS3Key = result.fileName;
      this.isLogoUploading.set(false);
    }).catch(() => {
      this.toast.error('Failed to upload logo. Please try again.');
      this.isLogoUploading.set(false);
    });
  }

  removeLogo(): void {
    this.logoS3Key = '';
    this.logoPreview.set(null);
  }

  async onSubmit(): Promise<void> {
    trimFormGroup(this.familyForm);
    if (this.familyForm.invalid) {
      this.familyForm.markAllAsTouched();
      return;
    }

    this.isLoading.set(true);
    const { familyName, welcomeMessage } = this.familyForm.value;
    const family = this.editFamily();

    let resp;
    if (family) {
      const body: UpdateFamilyGroupRequest = {
        familySpaceId:      family.id,
        familyName:         familyName!,
        displayPictureName: this.logoS3Key || '',
        welcomeMessage:     welcomeMessage || '',
      };
      resp = await this.api.request(
        'POST',
        API_ROUTES.UPDATE_FAMILY_GROUP,
        body,
        { showToaster: true, showLoader: false, useToken: true },
      );
    } else {
      const body: CreateFamilyGroupRequest = {
        familyName:         familyName!,
        displayPictureName: this.logoS3Key || '',
        welcomeMessage:     welcomeMessage || '',
      };
      resp = await this.api.request(
        'POST',
        API_ROUTES.CREATE_FAMILY_GROUP,
        body,
        { showToaster: true, showLoader: false, useToken: true },
      );
    }

    if (resp.status) {
      this.familyCreated.emit();
    }
    this.isLoading.set(false);
  }
}
